/**
 * GroupApplicationModal — Group membership application form
 * PROTEGE QV Lecture+
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Textarea from '../common/Textarea';
import Button from '../common/Button';
import { groupApplicationSchema } from '../../utils/validators';
import { saveDocument } from '../../services/firestoreService';
import { sendAdminEmail } from '../../services/emailService';
import { openWhatsApp } from '../../utils/whatsapp';

const ADMIN_PHONE = import.meta.env.VITE_WHATSAPP_NUMBER || '237699936028';

const GroupApplicationModal = ({ isOpen, onClose, group }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: zodResolver(groupApplicationSchema) });

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await saveDocument('group_applications', {
        groupId: group?.id || null,
        groupNom: group?.nom || group?.name || '',
        groupTheme: group?.theme || '',
        nom_complet: data.nom_complet,
        email: data.email,
        telephone: data.telephone || null,
        motivation: data.motivation,
        statut: 'en_attente',
      });

      await sendAdminEmail(
        `[PROTEGE QV] Nouvelle candidature — Groupe ${group?.nom || ''}`,
        `
          <h2>Nouvelle demande d'adhésion</h2>
          <p><strong>Groupe :</strong> ${group?.nom || ''} (${group?.theme || ''})</p>
          <hr/>
          <p><strong>Nom :</strong> ${data.nom_complet}</p>
          <p><strong>Email :</strong> ${data.email}</p>
          ${data.telephone ? `<p><strong>Téléphone :</strong> ${data.telephone}</p>` : ''}
          <h3>Motivation</h3>
          <p>${data.motivation.replace(/\n/g, '<br/>')}</p>
        `
      );

      openWhatsApp(
        ADMIN_PHONE,
        `👥 Nouvelle candidature PROTEGE QV\nGroupe : ${group?.nom || ''}\nCandidature de : ${data.nom_complet}\nEmail : ${data.email}`
      );

      toast.success(t('group_application.success'));
      reset();
      onClose();
    } catch (error) {
      console.error('Group application error:', error);
      toast.error(t('group_application.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${t('group_application.title')} — ${group?.nom || ''}`}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="group-application-form"
            loading={submitting}
          >
            {t('group_application.submit')}
          </Button>
        </>
      }
    >
      <form id="group-application-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label={t('contact.name')}
          {...register('nom_complet')}
          error={errors.nom_complet?.message}
          placeholder={t('contact.name_ph')}
          required
        />

        <Input
          label={t('contact.email')}
          type="email"
          {...register('email')}
          error={errors.email?.message}
          placeholder={t('contact.email_ph')}
          required
        />

        <Input
          label={t('contact.phone_label')}
          type="tel"
          {...register('telephone')}
          error={errors.telephone?.message}
          placeholder="+237 6XX XXX XXX"
        />

        <Textarea
          label={t('group_application.motivation_label')}
          {...register('motivation')}
          error={errors.motivation?.message}
          placeholder={t('group_application.motivation_ph')}
          rows={4}
          helperText="Minimum 10 caractères"
          required
        />
      </form>
    </Modal>
  );
};

export default GroupApplicationModal;
